// Pure helpers for the promotion Schedule / Applies-to controls (no React, no network, so they
// can be unit tested with `node --test`). The server is the authority on every rule here; these
// functions only mirror them so the form can explain itself and fail early.

export const SCHEDULE_TYPES = [
  { value: "always", label: "Always" },
  { value: "days", label: "Days of the week" },
  { value: "time", label: "Time of day" },
];

// ISO weekday numbers, as the API expects them: 1 = Monday ... 7 = Sunday.
export const WEEKDAYS = [
  { value: 1, short: "Mon", long: "Monday" },
  { value: 2, short: "Tue", long: "Tuesday" },
  { value: 3, short: "Wed", long: "Wednesday" },
  { value: 4, short: "Thu", long: "Thursday" },
  { value: 5, short: "Fri", long: "Friday" },
  { value: 6, short: "Sat", long: "Saturday" },
  { value: 7, short: "Sun", long: "Sunday" },
];

export const TARGET_TYPES = {
  category: { token: "c", label: "Category", plural: "Categories" },
  subcategory: { token: "s", label: "Sub-category", plural: "Sub-categories" },
  product: { token: "p", label: "Item", plural: "Items" },
};

const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

export const isValidTime = (value) => typeof value === "string" && TIME_RE.test(value);

const isPositiveInt = (value) => /^[1-9]\d*$/.test(String(value));

// ---------------------------------------------------------------------------------------------
// Time of day: 24h 'H:i' (what the API takes) <-> 12-hour parts (what the picker shows)
// ---------------------------------------------------------------------------------------------

/** '16:05' -> { hour12: 4, minute: 5, meridiem: 'PM' }; invalid input -> null. */
export const to12HourParts = (value) => {
  const m = TIME_RE.exec(value || "");
  if (!m) return null;
  const h = Number(m[1]);
  return { hour12: h % 12 === 0 ? 12 : h % 12, minute: Number(m[2]), meridiem: h < 12 ? "AM" : "PM" };
};

/** { hour12: 4, minute: 5, meridiem: 'PM' } -> '16:05'. 12 AM is 00:xx, 12 PM is 12:xx. */
export const from12HourParts = ({ hour12, minute, meridiem }) => {
  const h12 = Number(hour12);
  const min = Number(minute);
  if (!(h12 >= 1 && h12 <= 12) || !(min >= 0 && min <= 59) || (meridiem !== "AM" && meridiem !== "PM")) {
    return "";
  }
  const h24 = (h12 % 12) + (meridiem === "PM" ? 12 : 0);
  return `${String(h24).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
};

/** '16:00' -> '4:00 PM'; '' / invalid -> ''. */
export const formatTime12 = (value) => {
  const p = to12HourParts(value);
  if (!p) return "";
  return `${p.hour12}:${String(p.minute).padStart(2, "0")} ${p.meridiem}`;
};

/** '16:00','18:00' -> '4:00-6:00 PM'; '11:00','13:30' -> '11:00 AM-1:30 PM'. */
export const formatTimeRange = (from, to) => {
  const a = to12HourParts(from);
  const b = to12HourParts(to);
  if (!a || !b) return "";
  const clock = (p) => `${p.hour12}:${String(p.minute).padStart(2, "0")}`;
  if (a.meridiem === b.meridiem) return `${clock(a)}-${clock(b)} ${b.meridiem}`;
  return `${clock(a)} ${a.meridiem}-${clock(b)} ${b.meridiem}`;
};

// ---------------------------------------------------------------------------------------------
// Days of the week
// ---------------------------------------------------------------------------------------------

/** Sorted, de-duplicated list of valid ISO weekdays (1-7) from any array-ish input. */
export const normalizeDays = (days) => {
  if (!Array.isArray(days)) return [];
  const set = new Set();
  days.forEach((d) => {
    const n = Number(d);
    if (Number.isInteger(n) && n >= 1 && n <= 7) set.add(n);
  });
  return Array.from(set).sort((a, b) => a - b);
};

/** [3] -> 'Wednesdays'; [1,2,3,4,5] -> 'Mon-Fri'; [1,3,6] -> 'Mon, Wed, Sat'. */
export const describeDays = (days) => {
  const list = normalizeDays(days);
  if (list.length === 0) return "";
  const byValue = (v) => WEEKDAYS.find((d) => d.value === v);
  if (list.length === 7) return "Every day";
  if (list.length === 1) return `${byValue(list[0]).long}s`;
  // Collapse runs of 3+ consecutive days into 'Mon-Fri'; keep shorter runs as a plain list.
  const parts = [];
  let i = 0;
  while (i < list.length) {
    let j = i;
    while (j + 1 < list.length && list[j + 1] === list[j] + 1) j += 1;
    if (j - i >= 2) {
      parts.push(`${byValue(list[i]).short}-${byValue(list[j]).short}`);
    } else {
      for (let k = i; k <= j; k += 1) parts.push(byValue(list[k]).short);
    }
    i = j + 1;
  }
  return parts.join(", ");
};

// ---------------------------------------------------------------------------------------------
// Targets ('c:12' category, 's:45' sub-category, 'p:789' product)
// ---------------------------------------------------------------------------------------------

const TOKEN_TO_TYPE = Object.keys(TARGET_TYPES).reduce((acc, type) => {
  acc[TARGET_TYPES[type].token] = type;
  return acc;
}, {});

/** ('category', 12) -> 'c:12'; unknown type or non-positive-integer id -> null. */
export const encodeTarget = (type, id) => {
  const def = TARGET_TYPES[type];
  if (!def || !isPositiveInt(id)) return null;
  return `${def.token}:${Number(id)}`;
};

/** 'c:12' -> { type: 'category', id: 12 }; anything else -> null. */
export const parseTarget = (token) => {
  const m = /^([csp]):([1-9]\d*)$/.exec(String(token || ""));
  if (!m) return null;
  return { type: TOKEN_TO_TYPE[m[1]], id: Number(m[2]) };
};

/** De-duplicated list of valid tokens, original order kept. Invalid entries are dropped. */
export const encodeTargets = (selection) => {
  const seen = new Set();
  const out = [];
  (Array.isArray(selection) ? selection : []).forEach((entry) => {
    const token = typeof entry === "string" ? (parseTarget(entry) ? entry : null) : encodeTarget(entry?.type, entry?.id);
    if (token && !seen.has(token)) {
      seen.add(token);
      out.push(token);
    }
  });
  return out;
};

const cleanName = (value) => (typeof value === "string" ? value.trim() : "");

const cleanList = (list, build) => {
  const seen = new Set();
  const out = [];
  (Array.isArray(list) ? list : []).forEach((raw) => {
    if (!raw || typeof raw !== "object" || !isPositiveInt(raw.id)) return;
    const id = Number(raw.id);
    const name = cleanName(raw.name);
    if (!name || seen.has(id)) return; // nameless rows cannot be searched or understood
    seen.add(id);
    out.push(build(raw, id, name));
  });
  return out;
};

/**
 * Tolerates dirty / partial server data: non-array groups, rows without an id or a name,
 * duplicate ids and non-numeric ids are dropped. Never throws.
 * Returns { categories, subcategories, products } each as [{ token, type, id, name, parentLabel }].
 */
export const normalizeTargetOptions = (raw) => {
  const src = raw && typeof raw === "object" ? raw : {};
  const categories = cleanList(src.categories, (r, id, name) => ({
    token: encodeTarget("category", id),
    type: "category",
    id,
    name,
    parentLabel: "",
  }));
  const catName = new Map(categories.map((c) => [c.id, c.name]));
  const subcategories = cleanList(src.subcategories, (r, id, name) => ({
    token: encodeTarget("subcategory", id),
    type: "subcategory",
    id,
    name,
    parentLabel: catName.get(Number(r.category_id)) || "",
  }));
  const subName = new Map(subcategories.map((s) => [s.id, s.name]));
  const products = cleanList(src.products, (r, id, name) => ({
    token: encodeTarget("product", id),
    type: "product",
    id,
    name,
    parentLabel: subName.get(Number(r.subcategory_id)) || catName.get(Number(r.category_id)) || "",
  }));
  return { categories, subcategories, products };
};

/** Case-insensitive name search across all three groups; blank query returns everything. */
export const filterTargetOptions = (options, query) => {
  const q = cleanName(query).toLowerCase();
  const keep = (list) => (q ? list.filter((o) => o.name.toLowerCase().includes(q)) : list);
  return {
    categories: keep(options.categories),
    subcategories: keep(options.subcategories),
    products: keep(options.products),
  };
};

/** { category: n, subcategory: n, product: n, total: n } for the tokens in `selectedTokens`. */
export const countTargets = (selectedTokens) => {
  const counts = { category: 0, subcategory: 0, product: 0, total: 0 };
  encodeTargets(selectedTokens).forEach((token) => {
    counts[parseTarget(token).type] += 1;
    counts.total += 1;
  });
  return counts;
};

/** '3 selected: 1 category, 1 sub-category, 1 item'; zero -> 'None selected'. */
export const describeTargetCounts = (selectedTokens) => {
  const c = countTargets(selectedTokens);
  if (c.total === 0) return "None selected";
  const parts = [];
  if (c.category) parts.push(`${c.category} ${c.category === 1 ? "category" : "categories"}`);
  if (c.subcategory) parts.push(`${c.subcategory} ${c.subcategory === 1 ? "sub-category" : "sub-categories"}`);
  if (c.product) parts.push(`${c.product} ${c.product === 1 ? "item" : "items"}`);
  return `${c.total} selected: ${parts.join(", ")}`;
};

// ---------------------------------------------------------------------------------------------
// Validation, summary and request fields
// ---------------------------------------------------------------------------------------------

/**
 * Mirrors the server's rules. Returns an error message or null.
 * schedule = { type, days, timeFrom, timeTo }; targets = { mode: 'all'|'selected', tokens: [] }
 */
export const validateScheduleForm = (schedule, targets) => {
  const s = schedule || {};
  if (s.type === "days") {
    if (normalizeDays(s.days).length === 0) return "Pick at least one day of the week.";
  } else if (s.type === "time") {
    if (!isValidTime(s.timeFrom) || !isValidTime(s.timeTo)) return "Set both a start and an end time.";
    if (s.timeFrom >= s.timeTo) return "The start time must be before the end time (no overnight windows).";
  } else if (s.type !== "always") {
    return "Choose when this promotion applies.";
  }
  const t = targets || { mode: "all", tokens: [] };
  if (t.mode === "selected" && encodeTargets(t.tokens).length === 0) {
    return "Select at least one category, sub-category or item, or switch to Entire menu.";
  }
  return null;
};

/** 'Applies on Wednesdays' / 'Applies 4:00-6:00 PM daily' / 'Applies at all times'. */
export const describeWhen = (schedule) => {
  const s = schedule || {};
  if (s.type === "days") {
    const text = describeDays(s.days);
    if (!text) return "";
    return text === "Every day" ? "Applies every day" : `Applies on ${text}`;
  }
  if (s.type === "time") {
    const range = formatTimeRange(s.timeFrom, s.timeTo);
    return range ? `Applies ${range} daily` : "";
  }
  return "Applies at all times";
};

/**
 * One plain-language line, e.g. 'Applies on Wednesdays to: Category Pizza, Item Garlic Bread.'
 * `labels` maps token -> { type, name } for the current selection. Returns '' if the schedule is
 * not yet complete (so the line simply is not shown while the form is half filled in).
 */
export const summarizePromotion = (schedule, targets, labels) => {
  const when = describeWhen(schedule);
  if (!when) return "";
  const t = targets || { mode: "all", tokens: [] };
  if (t.mode !== "selected") return `${when} to the entire menu.`;
  const names = encodeTargets(t.tokens).map((token) => {
    const known = labels && labels[token];
    const parsed = parseTarget(token);
    const typeLabel = TARGET_TYPES[parsed.type].label;
    return known && known.name ? `${typeLabel} ${known.name}` : `${typeLabel} #${parsed.id}`;
  });
  if (names.length === 0) return "";
  return `${when} to: ${names.join(", ")}.`;
};

/**
 * The extra create-offer fields, as [key, value] pairs ready for FormData.append. Empty when the
 * feature flag is off or the promotion is a voucher, so the request is then byte-for-byte what it
 * was before this feature. Call validateScheduleForm first; this does not re-validate.
 */
export const buildScheduleFields = ({ enabled, isCoupon, schedule, targets }) => {
  if (!enabled || isCoupon) return [];
  const fields = [];
  const s = schedule || {};
  fields.push(["schedule_type", s.type]);
  if (s.type === "days") {
    normalizeDays(s.days).forEach((d) => fields.push(["schedule_days[]", String(d)]));
  } else if (s.type === "time") {
    fields.push(["schedule_time_from", s.timeFrom]);
    fields.push(["schedule_time_to", s.timeTo]);
  }
  if (targets && targets.mode === "selected") {
    encodeTargets(targets.tokens).forEach((token) => fields.push(["targets[]", token]));
  }
  return fields;
};

// ---------------------------------------------------------------------------------------------
// Reading the server's responses (every field may be missing on an old backend)
// ---------------------------------------------------------------------------------------------

/** True only when the response explicitly says the feature is on. Accepts true / 1 / '1'. */
export const readOfferScheduleFlag = (body) => {
  const candidates = [body && body.features, body && body.data && !Array.isArray(body.data) ? body.data.features : null];
  for (const features of candidates) {
    if (features && typeof features === "object") {
      const v = features.offer_schedule;
      return v === true || v === 1 || v === "1";
    }
  }
  return false;
};

const textOr = (value, fallback) => (typeof value === "string" && value.trim() ? value.trim() : fallback);

/** Listing cell text for the Schedule column. */
export const scheduleCellText = (promo) => textOr(promo && promo.schedule && promo.schedule.text, "Always");

/** Listing cell text for the Applies-to column. */
export const targetsCellText = (promo) => textOr(promo && promo.targets_text, "Entire menu");

/** true / false when the server said so, null when the field is absent (then show nothing). */
export const eligibleNowState = (promo) => {
  const v = promo ? promo.eligible_now : undefined;
  if (v === true || v === 1 || v === "1") return true;
  if (v === false || v === 0 || v === "0") return false;
  return null;
};
